import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Icon } from "@iconify/react/dist/iconify.js";

const TopBlockedIpsChart = ({ data }) => {
  const [selectedIp, setSelectedIp] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  };

  // Aggregate all daily data to get total blocks per day
  const aggregateDailyData = () => {
    if (!data || data.length === 0) return [];
    
    // Create a map to store aggregated counts by day
    const dailyMap = {};
    
    // Iterate through all IPs and their daily data
    data.forEach(ipData => {
      if (ipData.daily && ipData.daily.length > 0) {
        ipData.daily.forEach(day => {
          if (!dailyMap[day.day]) {
            dailyMap[day.day] = { day: day.day, count: 0 };
          }
          dailyMap[day.day].count += day.count;
        });
      }
    });
    
    // Convert map to array and sort by day
    return Object.values(dailyMap).sort((a, b) => new Date(a.day) - new Date(b.day));
  };
  
  const overviewData = aggregateDailyData();
  
  // Format data for the IP list
  const ipListData = data.map(item => ({
    ip: item.ip,
    count: item.count
  })).sort((a, b) => b.count - a.count); // Sort for list (descending)

  // Get daily data for selected IP or first IP if none selected
  const getSelectedIpData = () => {
    if (!data || data.length === 0) return [];
    
    const ipData = selectedIp 
      ? data.find(item => item.ip === selectedIp)
      : data[0];
      
    return ipData?.daily || [];
  };

  const dailyData = getSelectedIpData();
  const currentIp = selectedIp || (data && data.length > 0 ? data[0].ip : 'N/A');

  // Calculate total blocks
  const totalBlocks = data?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;

  return (
    <div 
      className={`
        flex flex-col 
        card-container 
        rounded-md 
        p-2 sm:p-3 
        w-full 
        ${showDetails ? 'h-auto' : 'h-full'}
      `}
    >
      <div className="flex flex-row justify-between items-center mb-2">
        <div className="text-lg font-semibold text-font-primary">
          Top Blocked IP Addresses
        </div>
        <button 
          className="flex flex-row items-center gap-1 text-sm text-font-primary hover:text-primary transition-colors p-2 rounded-md button-container"
          onClick={() => setShowDetails(!showDetails)}
        >
          <span>Details</span>
          <Icon 
            icon={showDetails ? "mingcute:up-line" : "mingcute:down-line"} 
            className="text-lg text-primary" 
          />
        </button>
      </div>
      
      <div className="flex flex-row items-center justify-between">
        <div className="text-sm text-font-secondary">Total Blocks: <span className="font-medium text-primary">{totalBlocks}</span></div>
        {selectedIp && (
          <div className="text-sm text-font-secondary">Selected IP: <span className="font-medium text-primary">{selectedIp}</span></div>
        )}
      </div>
      
      {/* Details Table */}
      {showDetails && (
        <div className="mb-4 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left py-2 text-xs text-font-secondary">IP Address</th>
                <th className="text-right py-2 text-xs text-font-secondary">Block Count</th>
              </tr>
            </thead>
            <tbody>
              {ipListData.map((item, index) => (
                <tr 
                  key={index} 
                  className={`border-t border-bgAccent hover:bg-bgAccent cursor-pointer ${selectedIp === item.ip ? 'bg-bgAccent' : ''}`}
                  onClick={() => setSelectedIp(item.ip)}
                >
                  <td className="py-2 text-sm">{item.ip}</td>
                  <td className="text-right py-2 text-sm">{item.count}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr className="border-t border-gray-200">
                  <td colSpan={2} className="text-center py-4 text-sm">No blocked IPs to display</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Overview horizontal bar chart showing all top IPs */}
        <div className="h-[250px]">
          <div className="flex items-center justify-end mb-2">
            <div className="flex items-center gap-1 text-xs text-font-secondary">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ff4d4f' }}></div>
              <span>Blocked Requests by Day</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={overviewData} 
              margin={{ top: 5, right: 0, left: -25, bottom: 25 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                opacity={0.2} 
              />
              <XAxis 
                dataKey="day" 
                tickFormatter={formatDate} 
                stroke="#aaa" 
              />
              <YAxis 
                stroke="#aaa" 
                allowDecimals={false} 
              />
              <Tooltip 
                formatter={(value) => [value, 'Blocked Requests']} 
                labelFormatter={formatDate} 
              />
              <Bar 
                dataKey="count" 
                name="Total Blocked Requests" 
                fill="#ff4d4f" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Daily trend for selected IP */}
        <div className="h-[250px]">
          <div className="text-sm font-medium mb-2">Daily Activity for IP: {currentIp}</div>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart 
              data={dailyData}
              margin={{ top: 5, right: 0, left: -25, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                opacity={0.2} 
              />
              <XAxis 
                dataKey="day" 
                tickFormatter={formatDate} 
                stroke="#aaa" 
              />
              <YAxis 
                stroke="#aaa" 
                allowDecimals={false} 
              />
              <Tooltip 
                formatter={(value) => [value, 'Blocked Requests']} 
                labelFormatter={formatDate} 
              />
              <Bar 
                dataKey="count" 
                name="Blocked Requests" 
                fill="#3189E0" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TopBlockedIpsChart;